<script lang="ts" setup>
import { useTemplateRef } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import {
  dropdownHeight,
  useSearchSuggestions,
} from '@/utils/composables/useSearchSuggestions';

interface Props {
  modelValue?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  search: [];
  'delete-history': [];
}>();

const historyRefs = useTemplateRef('historyRefs');
const autocompleteRefs = useTemplateRef('autocompleteRefs');

const {
  inputValue,
  handledValue,
  handlers,
  onKeyDown,
  searchHistories,
  historyFocusIndex,
  hoveredIndex,
  isDisplayedHistories,
  selectHistory,
  deleteHistory,
  completions,
  autocompleteFocusIndex,
  isAutocompleteEnabled,
  selectAutocomplete,
} = useSearchSuggestions(
  () => props.modelValue,
  { historyRefs, autocompleteRefs },
  {
    onUpdateModelValue: value => emit('update:modelValue', value),
    onSearch: () => emit('search'),
    onDeleteHistory: () => emit('delete-history'),
  },
);

const SURVEY_NAVIGATION_ITEM_HEIGHT = 76;
</script>

<template>
  <div>
    <slot :value="inputValue" :handlers="handlers">
      <input
        class="c-text-regular--m c-textInput"
        :value="inputValue"
        v-on="handlers"
      />
    </slot>
    <!-- 検索履歴 -->
    <div
      v-if="isDisplayedHistories && searchHistories.length > 0"
      class="dropdown"
      :class="{ open: true }"
      :style="{
        '--dropdown-height': dropdownHeight(searchHistories.length) + 'px',
      }"
    >
      <div
        v-for="(searchHistory, index) in searchHistories"
        :key="`${index}_${searchHistory.query}`"
        ref="historyRefs"
        class="item"
        :class="{ focused: historyFocusIndex === index }"
        :data-index="index"
        @click="selectHistory(index)"
        @keydown="onKeyDown"
        @mouseover="hoveredIndex = index"
        @mouseleave="hoveredIndex = -1"
      >
        <div class="item-content">
          <DgrIcon class="clock-icon" name="clock" :size="'default'" />
          <span class="c-text-regular--m search-history">{{
            searchHistory.query
          }}</span>
        </div>
        <button
          type="button"
          class="delete-button"
          @click.stop="deleteHistory(searchHistory.query)"
          v-if="historyFocusIndex === index || hoveredIndex === index"
        >
          <DgrIcon class="delete-icon" name="times" :size="'xs'" />
        </button>
      </div>
    </div>
    <!-- キーワード補完 -->
    <div
      v-if="isAutocompleteEnabled"
      class="dropdown"
      :class="{ open: true }"
      :style="{
        '--dropdown-height':
          dropdownHeight(completions.length) +
          SURVEY_NAVIGATION_ITEM_HEIGHT +
          'px',
      }"
    >
      <div
        v-for="(completionItem, index) in completions"
        :key="`${index}_${completionItem.keyword}`"
        ref="autocompleteRefs"
        class="item"
        :class="{ focused: autocompleteFocusIndex === index }"
        :data-index="index"
        @click="selectAutocomplete(index)"
        @keydown="onKeyDown"
      >
        <div class="item-content">
          <DgrIcon class="search-icon" name="search" />
          <span class="c-text-regular--m">{{
            completionItem.splitKeywords[0]
          }}</span>
          <span class="c-title-bold--m">{{
            completionItem.splitKeywords[1]
          }}</span>
        </div>
      </div>
      <!-- キーワードサジェストの最後の要素は要約結果画面への動線 -->
      <div
        class="survey-navigation-item"
        :class="{ focused: autocompleteFocusIndex === completions.length }"
        :data-index="completions.length"
        @click="selectAutocomplete(completions.length)"
        @keydown="onKeyDown"
      >
        <p class="c-text-regular--s navigation-title">要約を生成</p>
        <p class="item-content">
          <DgrIcon name="sparkles-fill" class="spark-icon" />
          <span class="c-text-regular--m">{{ handledValue }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  position: absolute;
  z-index: var(--z-dropdown);
  background: white;
  width: 100%;
  max-width: 520px; // keyword-search-bar.vueの".c-textInput"に合わせる
  max-height: 0;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  border-radius: 4px;
  transition: max-height 0.1s ease-out;

  &.open {
    max-height: var(--dropdown-height);
  }
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  max-width: 520px;
  border: none;
  background: white;
  padding: 0 8px;
  margin: 0;

  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }

  &.focused {
    background: $color-gray400;
  }
}

.survey-navigation-item {
  padding: 8px;
  align-items: center;
  justify-content: space-between;
  height: 52px;

  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }

  &.focused {
    background: $color-gray400;
  }
}

.navigation-title {
  padding-bottom: 8px;
  color: $color-gray800;
}

.item-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
}

.clock-icon,
.search-icon,
.spark-icon {
  flex-shrink: 0;
  margin-right: 8px;
}

.delete-button {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  margin: 0 8px;
}

.delete-icon {
  flex-shrink: 0;
}

.search-history {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  min-width: 0;
}
</style>
